/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Button, Collapse, ListItem, makeStyles, SvgIcon, ListItemText } from '@material-ui/core';
import { useSelector, shallowEqual } from 'react-redux';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { PAPELDEFAULT } from '../../utils/rbac/papeis';
import { svgIcons } from '../../utils/icons';

const useStyles = makeStyles((theme) => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: theme.palette.text.secondary,
    padding: '9px 5px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
  },
  buttonLeaf: {
    color: theme.palette.text.secondary,
    padding: '5px 5px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium,
      }
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  title: {
    marginRight: 'auto',
  },
  active: {
    color: theme.palette.secondary.main,
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: theme.palette.secondary.main
    }
  },
  activeButtonLeaf: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.common.black,
      '& $icon': {
        color: theme.palette.common.black
      }
    },
    '& $title': {
      fontWeight: theme.typography.fontWeightMedium
    },
    '& $icon': {
      color: theme.palette.common.white,
      '&:hover': {
        color: theme.palette.common.black
      }
    }
  }
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  entidadeNome,
  svgIcon,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);
  const { myPrivilege } = useSelector(
    (state) => ({
      myPrivilege: String(state.account.user?.privilegeHash || PAPELDEFAULT)
    }),
    shallowEqual
  );

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button className={classes.button} onClick={handleToggle} style={style}>
          {Icon && svgIcon === false &&
            <Icon className={classes.icon} size="24" />
          }
          {Icon && svgIcon && (
            <SvgIcon
              component={svgIcons[Icon]}
              className={classes.icon}
              size="20"
            />
          )}
          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      <Button
        activeClassName={svgIcon ? classes.activeButtonLeaf : classes.active}
        className={clsx(classes.buttonLeaf, `depth-${depth}`)}
        component={RouterLink}
        exact
        style={style}
        to={href}
      >
        {Icon && svgIcon === false &&
          <Icon className={classes.icon} size="24" />
        }
        {Icon && svgIcon && (
          <SvgIcon
            component={svgIcons[Icon]}
            className={classes.icon}
            size="20"
          />
        )}
        <ListItemText
          primary={title}
          primaryTypographyProps={{ component: "span", variant: "subtitle2" }}
          secondaryTypographyProps={{component: 'p', color: 'inherit', style: { fontSize: '0.70rem' }}}
          secondary={entidadeNome} />
        {Info && <Info />}
      </Button>
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  entidadeNome: PropTypes.string.isRequired,
  svgIcon: PropTypes.bool
};

NavItem.defaultProps = {
  open: false,
  svgIcon: false
};

export default NavItem;
